import { IProductRepository } from '../interfaces/IProductRepository';
import { Product } from '../../types';
import { supabase } from '../../lib/supabase';
import { mapDbProductToFrontend, mapFrontendToDbProduct } from '../../utils/mappers';
import { createSearchPattern } from '../../utils/textUtils';

/**
 * API implementation del Product Repository (Mirror Mode).
 * Actualizado para la nueva jerarquía: Producto -> Variante -> Modelos
 */
const BASE_QUERY = '*, brand:brands(name), category:categories(name), variants:product_variants(*, models:product_variant_models(*, model:device_models(name)))';

export class ApiProductRepository implements IProductRepository {
    private async resolveDeviceModelId(nameOrId: string): Promise<string | null> {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(nameOrId)) return nameOrId;

        const { data } = await supabase
            .from('device_models')
            .select('id')
            .ilike('name', nameOrId)
            .limit(1);

        return data?.[0]?.id || null;
    }

    private async resolveBrandId(nameOrId: string): Promise<string | null> {
        if (!nameOrId) return null;
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(nameOrId)) return nameOrId;

        const { data } = await supabase
            .from('brands')
            .select('id')
            .ilike('name', nameOrId)
            .limit(1);

        return data?.[0]?.id || null;
    }

    private async resolveCategoryId(nameOrId: string): Promise<string | null> {
        if (!nameOrId) return null;
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(nameOrId)) return nameOrId;

        const { data } = await supabase
            .from('categories')
            .select('id')
            .ilike('name', nameOrId)
            .limit(1);

        return data?.[0]?.id || null;
    }

    async getMetadata(): Promise<{ brands: string[], categories: string[], models: string[] }> {
        const { data: brands } = await supabase.from('brands').select('name').order('name');
        const { data: categories } = await supabase.from('categories').select('name').order('name');
        const { data: models } = await supabase.from('device_models').select('name').order('name');

        return {
            brands: (brands || []).map(b => b.name),
            categories: (categories || []).map(c => c.name),
            models: (models || []).map(m => m.name)
        };
    }

    async getAll(): Promise<Product[]> {
        try {
            const { data, error } = await supabase
                .from('products')
                .select(BASE_QUERY)
                .order('created_at', { ascending: false });

            if (error) throw error;
            return (data || []).map(mapDbProductToFrontend);
        } catch (error) {
            console.error('ApiProductRepository.getAll failed:', error);
            return [];
        }
    }

    async getById(id: string): Promise<Product | null> {
        try {
            const { data, error } = await supabase
                .from('products')
                .select(BASE_QUERY)
                .eq('id', id)
                .single();

            if (error) return null;
            return mapDbProductToFrontend(data);
        } catch (error) {
            console.error(`ApiProductRepository.getById(${id}) failed:`, error);
            return null;
        }
    }

    async getByBrand(brandSlug: string): Promise<Product[]> {
        // En Supabase, para filtrar por la relación usamos inner joins o subconsultas.
        // Dado el esquema, podemos filtrar por brand_id buscando el brand_id primero
        const { data: brandDataRaw } = await supabase.from('brands').select('id').eq('slug', brandSlug).limit(1);
        const brandData = brandDataRaw?.[0];
        if (!brandData) return [];

        const { data, error } = await supabase
            .from('products')
            .select(BASE_QUERY)
            .eq('brand_id', brandData.id)
            .eq('is_active', true);

        if (error) return [];
        return (data || []).map(mapDbProductToFrontend);
    }

    async getByCategory(categorySlug: string): Promise<Product[]> {
        const { data: categoryDataRaw } = await supabase.from('categories').select('id').eq('slug', categorySlug).limit(1);
        const categoryData = categoryDataRaw?.[0];
        if (!categoryData) return [];

        const { data, error } = await supabase
            .from('products')
            .select(BASE_QUERY)
            .eq('category_id', categoryData.id)
            .eq('is_active', true);

        if (error) return [];
        return (data || []).map(mapDbProductToFrontend);
    }

    async search(query: string): Promise<Product[]> {
        const pattern = createSearchPattern(query);
        const { data, error } = await supabase
            .from('products')
            .select(BASE_QUERY)
            .or(`name.ilike.${pattern},base_sku.ilike.${pattern},description.ilike.${pattern}`)
            .eq('is_active', true);

        if (error) return [];
        return (data || []).map(mapDbProductToFrontend);
    }

    async getPaginated(
        page: number,
        limit: number,
        query?: string,
        filters?: { category?: string[]; make?: string[]; model?: string[] }
    ): Promise<{ data: Product[]; total: number }> {
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        let dbQuery = supabase
            .from('products')
            .select(BASE_QUERY, { count: 'exact' })
            .order('created_at', { ascending: false })
            .range(from, to);

        if (query) {
            const pattern = createSearchPattern(query);
            dbQuery = dbQuery.or(`name.ilike.${pattern},base_sku.ilike.${pattern}`);
        }

        // Filters can be complex with joins in PostgREST, often requiring custom RPC 
        // or filtering on the frontend if the nested relationships are deep (like models)
        // For simple fields (category, brand), we would need to map names to IDs first
        // As a simplified fallback for this transition, we return all matches from the base filters

        const { data, error, count } = await dbQuery;

        if (error) {
            console.error('ApiProductRepository.getPaginated failed:', error);
            return { data: [], total: 0 };
        }

        let results = (data || []).map(mapDbProductToFrontend);

        // Client-side filtering fallback for complex related fields
        if (filters?.category && filters.category.length > 0) {
            results = results.filter(p => filters.category!.includes(p.category || ''));
        }
        if (filters?.make && filters.make.length > 0) {
            results = results.filter(p => filters.make!.includes(p.make || ''));
        }
        if (filters?.model && filters.model.length > 0) {
            results = results.filter(p =>
                p.variants?.some(v =>
                    v.models?.some(m => filters.model!.includes(m.model_name || ''))
                )
            );
        }

        return {
            data: results,
            total: count || 0
        };
    }

    async toggleVisibility(productId: string, currentStatus: boolean): Promise<void> {
        const { error } = await supabase
            .from('products')
            .update({ is_active: !currentStatus })
            .eq('id', productId);

        if (error) throw error;
    }

    async getAvailableInBranch(_branchId: string): Promise<Product[]> {
        const { data, error } = await supabase
            .from('products')
            .select(BASE_QUERY)
            .eq('is_active', true);

        if (error) return [];
        const mapped = (data || []).map(mapDbProductToFrontend);
        // Filtra los que tengan al menos una variante con stock
        return mapped.filter(p => p.stock?.['online'] === true);
    }

    async updateStock(productId: string, branchId: string, inStock: boolean): Promise<void> {
        throw new Error("Method not implemented.");
    }

    async checkRealtimeAvailability(productId: string, variantId?: string, modelId?: string): Promise<boolean> {
        try {
            if (modelId) {
                // If we have a specific model, just check that model's stock
                const { data, error } = await supabase
                    .from('product_variant_models')
                    .select('in_stock, is_active')
                    .eq('id', modelId)
                    .single();
                if (error) throw error;
                return data ? (data.in_stock && data.is_active) : false;
            } else {
                // Check if ANY model for this product is in stock
                const { data, error } = await supabase
                    .from('product_variants')
                    .select('is_active, models:product_variant_models(in_stock, is_active)')
                    .eq('product_id', productId)
                    .eq('is_active', true);

                if (error || !data || data.length === 0) {
                    return true; // Fallback for backwards compatibility
                }

                return data.some(v => v.models.some((m: any) => m.in_stock && m.is_active));
            }
        } catch (error) {
            console.error('Error checking realtime availability:', error);
            // Fallback to true so we don't block checkout if DB fails or offline. 
            // Allows WhatsApp to open (fail-open) which is better for sales than a broken button.
            return true;
        }
    }

    async create(product: Omit<Product, 'id'>): Promise<Product> {
        const dbPayload = mapFrontendToDbProduct(product);

        // Resolve string names from SearchableSelect back to UUIDs
        if (product.brand_id) {
            const resolvedBrandId = await this.resolveBrandId(product.brand_id);
            if (resolvedBrandId) dbPayload.brand_id = resolvedBrandId;
        }
        if (product.category_id) {
            const resolvedCategoryId = await this.resolveCategoryId(product.category_id);
            if (resolvedCategoryId) dbPayload.category_id = resolvedCategoryId;
        }

        // 1. Create Product
        const { data: productData, error: productError } = await supabase
            .from('products')
            .insert(dbPayload)
            .select()
            .single();

        if (productError) {
            console.error('Error creating product:', productError);
            throw productError;
        }

        // 2. Create Variants AND Models
        if (product.variants && product.variants.length > 0) {
            for (let i = 0; i < product.variants.length; i++) {
                const v = product.variants[i];

                // Inject main product images to the first variant as a fallback persistence layer
                // Insert Variant (No fallback needed anymore since products table now has image and images columns)
                const { data: variantData, error: variantError } = await supabase
                    .from('product_variants')
                    .insert({
                        product_id: productData.id,
                        color_name: v.color_name,
                        color_hex: v.color_hex,
                        color_code: v.color_code,
                        image_url: v.image_url,
                        texture_url: v.texture_url,
                        images: v.images || [],
                        variant_sku: v.variant_sku,
                        is_active: v.is_active ?? true
                    })
                    .select()
                    .single();

                if (variantError) {
                    console.error('Error creating variant:', variantError);
                    continue; // Skip models if variant fails
                }

                // Insert Models for this Variant
                if (v.models && v.models.length > 0) {
                    const modelsPayload = [];
                    for (const m of v.models) {
                        const modelId = await this.resolveDeviceModelId(m.model_id);
                        if (modelId) {
                            modelsPayload.push({
                                variant_id: variantData.id,
                                model_id: modelId,
                                in_stock: m.in_stock,
                                is_active: m.is_active ?? true,
                                image_url: m.image_url
                            });
                        }
                    }

                    if (modelsPayload.length > 0) {
                        const { error: modelsError } = await supabase
                            .from('product_variant_models')
                            .insert(modelsPayload);

                        if (modelsError) console.error('Error inserting models:', modelsError);
                    }
                }
            }
        }

        return this.getById(productData.id) as Promise<Product>;
    }

    async update(id: string, product: Partial<Product>): Promise<Product> {
        const dbPayload = mapFrontendToDbProduct(product);

        // Resolve string names from SearchableSelect back to UUIDs
        if (product.brand_id) {
            const resolvedBrandId = await this.resolveBrandId(product.brand_id);
            if (resolvedBrandId) dbPayload.brand_id = resolvedBrandId;
        }
        if (product.category_id) {
            const resolvedCategoryId = await this.resolveCategoryId(product.category_id);
            if (resolvedCategoryId) dbPayload.category_id = resolvedCategoryId;
        }

        // 1. Update Product
        const { error: productError } = await supabase
            .from('products')
            .update(dbPayload)
            .eq('id', id);

        if (productError) {
            console.error('Error updating product:', productError);
            throw productError;
        }

        // 2. Handle Variants & Models (Upsert Strategy to preserve UUIDs and ordering)
        if (product.variants) {
            const currentVariantIds = product.variants
                .map(v => v.id)
                .filter(id => id && !id.startsWith('temp-'));

            // Delete variants that were removed by the user
            if (currentVariantIds.length > 0) {
                const { error: tempDeleteError } = await supabase
                    .from('product_variants')
                    .delete()
                    .eq('product_id', id)
                    .not('id', 'in', currentVariantIds);
                if (tempDeleteError) console.error('Error deleting removed variants:', tempDeleteError);
            } else {
                // All variants were removed
                const { error: tempDeleteError } = await supabase
                    .from('product_variants')
                    .delete()
                    .eq('product_id', id);
                if (tempDeleteError) console.error('Error deleting all variants:', tempDeleteError);
            }

            for (let i = 0; i < product.variants.length; i++) {
                const v = product.variants[i];
                const isNewVariant = !v.id || v.id.startsWith('temp-');
                let variantData = null;

                const variantPayload = {
                    product_id: id,
                    color_name: v.color_name,
                    color_hex: v.color_hex,
                    color_code: v.color_code,
                    image_url: v.image_url,
                    texture_url: v.texture_url,
                    images: v.images || [],
                    variant_sku: v.variant_sku,
                    is_active: v.is_active ?? true
                };

                if (isNewVariant) {
                    const { data, error } = await supabase.from('product_variants').insert(variantPayload).select().single();
                    if (error) {
                        console.error('Error creating variant:', error);
                    } else {
                        variantData = data;
                    }
                } else {
                    const { data, error } = await supabase.from('product_variants').update(variantPayload).eq('id', v.id).select().single();
                    if (error) {
                        console.error('Error updating variant:', error);
                    } else {
                        variantData = data;
                    }
                }

                if (!variantData) continue;

                if (v.models && v.models.length > 0) {
                    const currentModelIds = v.models
                        .map(m => m.id)
                        .filter(id => id && !id.startsWith('temp-'));

                    // Remove models the user deleted
                    if (currentModelIds.length > 0) {
                        const { error: mDeleteError } = await supabase.from('product_variant_models')
                            .delete().eq('variant_id', variantData.id).not('id', 'in', currentModelIds);
                        if (mDeleteError) console.error('Error deleting models:', mDeleteError);
                    } else {
                        const { error: mDeleteError } = await supabase.from('product_variant_models')
                            .delete().eq('variant_id', variantData.id);
                        if (mDeleteError) console.error('Error deleting models:', mDeleteError);
                    }

                    const modelsPayloadToInsert = [];
                    for (const m of v.models) {
                        const modelId = await this.resolveDeviceModelId(m.model_id);
                        if (!modelId) continue;

                        const isNewModel = !m.id || m.id.startsWith('temp-');
                        const modelPayload = {
                            variant_id: variantData.id,
                            model_id: modelId,
                            in_stock: m.in_stock,
                            is_active: m.is_active ?? true,
                            image_url: m.image_url
                        };

                        if (isNewModel) {
                            modelsPayloadToInsert.push(modelPayload);
                        } else {
                            // Update existing model link
                            await supabase.from('product_variant_models').update(modelPayload).eq('id', m.id);
                        }
                    }

                    if (modelsPayloadToInsert.length > 0) {
                        const { error: modelsError } = await supabase.from('product_variant_models').insert(modelsPayloadToInsert);
                        if (modelsError) console.error('Error inserting models:', modelsError);
                    }
                } else {
                    // No models for this variant, clear them
                    await supabase.from('product_variant_models').delete().eq('variant_id', variantData.id);
                }
            }
        }

        return this.getById(id) as Promise<Product>;
    }

    async delete(id: string): Promise<void> {
        const { error } = await supabase
            .from('products')
            .delete()
            .eq('id', id);

        if (error) throw error;
    }
}
